import React, { useState, useEffect } from 'react'
import {
  Card, Table, Container, Button, Badge, InputGroup, Form,
  Modal, Row, Col, OverlayTrigger, Tooltip, Alert, ProgressBar, Spinner
} from 'react-bootstrap'
import { QRCodeSVG } from 'qrcode.react'
import { supabase } from '../services/supabase'
import Layout from '../components/Layout'
import ProtectedRoute from '../components/ProtectedRoute'

// ─── Couleurs par sport ───────────────────────────────────────────────────────
const SPORT_COLORS = {
  default: ['#4f46e5', '#7c3aed', '#db2777', '#ea580c', '#16a34a', '#0891b2', '#ca8a04']
}
const getSportColor = (index) => SPORT_COLORS.default[index % SPORT_COLORS.default.length]

// ─── Jours de la semaine en français ─────────────────────────────────────────
const DAYS_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']

// ─── Profil complet : nom, prénom, date de naissance + (email ou téléphone) ──
const isProfileComplete = (member) => {
  return !!(member.first_name && member.last_name && member.birth_date && (member.email || member.phone))
}

// ─── Âge exact à partir de la date de naissance (AAAA-MM-JJ) ─────────────────
const getAge = (birthDate) => {
  if (!birthDate) return null
  const [y, m, d] = String(birthDate).substring(0, 10).split('-').map(Number)
  if (!y || !m || !d) return null
  const now = new Date()
  let age = now.getFullYear() - y
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age--
  return age
}

// ─── Mineur (< 18 ans) dont le profil n'est pas encore complet ───────────────
const isMinorIncomplete = (member) => {
  const age = getAge(member.birth_date)
  return age !== null && age < 18 && !isProfileComplete(member)
}

function Members() {
  const [members, setMembers] = useState([])
  const [filteredMembers, setFilteredMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [minorsOnly, setMinorsOnly] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)

  // ─── Stats ────────────────────────────────────────────────────────────────
  const [showStatsModal, setShowStatsModal] = useState(false)
  const [statsMember, setStatsMember] = useState(null)
  const [statsData, setStatsData] = useState(null)
  const [statsLoading, setStatsLoading] = useState(false)

  // ─── Modal QR Code lien de profil ────────────────────────────────────────
  const [showProfileQRModal, setShowProfileQRModal] = useState(false)
  const [profileQRMember, setProfileQRMember] = useState(null)

  // ─── Modal Fiche membre complète ──────────────────────────────────────────
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [profileMember, setProfileMember] = useState(null)

  // ─── Fusion doublons ──────────────────────────────────────────────────────
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [duplicateGroups, setDuplicateGroups] = useState([])
  const [merging, setMerging] = useState(false)
  const [mergeResult, setMergeResult] = useState(null)

  // ─── Édition membre ───────────────────────────────────────────────────────
  const [showEditModal, setShowEditModal] = useState(false)
  const [editMember, setEditMember] = useState(null)
  const [editSaving, setEditSaving] = useState(false)
  const [editSuccess, setEditSuccess] = useState(false)

  useEffect(() => { fetchMembers() }, [])

  useEffect(() => {
    const term = searchTerm.toLowerCase()
    let list = members
    if (term) {
      list = list.filter((m) =>
        (m.first_name || '').toLowerCase().includes(term) ||
        (m.last_name || '').toLowerCase().includes(term) ||
        (m.nationality || '').toLowerCase().includes(term)
      )
    }
    if (minorsOnly) list = list.filter(isMinorIncomplete)
    setFilteredMembers(list)
  }, [members, searchTerm, minorsOnly])

  const fetchMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('participants').select('*').order('created_at', { ascending: false })
      if (error) throw error
      setMembers(data || [])
    } catch (error) {
      console.error('Error fetching members:', error)
    } finally {
      setLoading(false)
    }
  }

  // ─── Calcule les champs renseignés et le % de complétion ─────────────────
  const getProfileCompletionFields = (member) => {
    const fields = [
      { label: 'Nom',            value: member.last_name },
      { label: 'Prénom',         value: member.first_name },
      { label: 'Date naissance', value: member.birth_date },
      { label: 'Nationalité',    value: member.nationality },
      { label: 'Sexe',           value: member.sex },
      { label: 'État civil',     value: member.marital_status },
      { label: 'Email',          value: member.email },
      { label: 'Téléphone',      value: member.phone },
      { label: 'Tél. mobile',    value: member.phone_mobile },
      { label: 'Adresse',        value: member.address },
      { label: 'Contact urgence',value: member.emergency_contact },
    ]
    const filled = fields.filter(f => !!f.value).length
    return { fields, filled, total: fields.length, percent: Math.round((filled / fields.length) * 100) }
  }

  // ─── Ouvrir le modal fiche membre ────────────────────────────────────────
  const openProfileModal = (member) => { setProfileMember(member); setShowProfileModal(true) }

  // ─── Impression de la fiche membre ───────────────────────────────────────
  const printMemberProfile = (member) => {
    const age = member.birth_date
      ? Math.floor((new Date() - new Date(member.birth_date)) / (365.25 * 24 * 3600 * 1000))
      : null
    const printContent = `
      <!DOCTYPE html><html>
      <head>
        <title>Fiche membre — ${member.first_name} ${member.last_name}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; padding: 28px; color: #212529; font-size: 13px; }
          .header { text-align: center; margin-bottom: 24px; border-bottom: 3px solid #16a34a; padding-bottom: 16px; }
          .header .logo { font-size: 2rem; }
          .header h1 { color: #15803d; font-size: 20px; margin: 6px 0 2px; }
          .header h2 { font-size: 16px; color: #111; }
          .header .subtitle { font-size: 12px; color: #6b7280; margin-top: 4px; }
          .badge-complete { display:inline-block; background:#16a34a; color:#fff; padding:3px 12px; border-radius:20px; font-size:11px; font-weight:700; margin-top:8px; }
          .section { margin-bottom: 20px; }
          .section-title { font-size: 11px; font-weight: 700; color: #15803d; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #dcfce7; padding-bottom: 4px; margin-bottom: 10px; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 32px; }
          .field-label { font-size: 10px; color: #9ca3af; font-weight: 600; text-transform: uppercase; }
          .field-value { font-size: 13px; color: #111; font-weight: 500; border-bottom: 1px solid #f3f4f6; padding-bottom: 3px; margin-bottom: 4px; }
          .field-value.empty { color: #d1d5db; font-style: italic; }
          .footer { margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; display: flex; justify-content: space-between; }
          @media print { body { padding: 14px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">🥊</div>
          <h1>Street Sport Luxembourg</h1>
          <h2>${member.first_name} ${member.last_name}${age ? ` (${age} ans)` : ''}</h2>
          <div class="subtitle">Membre depuis le ${new Date(member.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
          <div class="badge-complete">✓ Profil complet</div>
        </div>
        <div class="section">
          <div class="section-title">Informations personnelles</div>
          <div class="grid">
            <div><div class="field-label">Nom</div><div class="field-value">${member.last_name || '—'}</div></div>
            <div><div class="field-label">Prénom</div><div class="field-value">${member.first_name || '—'}</div></div>
            <div><div class="field-label">Date de naissance</div><div class="field-value">${member.birth_date ? new Date(member.birth_date).toLocaleDateString('fr-FR') : '—'}</div></div>
            <div><div class="field-label">Nationalité</div><div class="field-value ${!member.nationality ? 'empty' : ''}">${member.nationality || 'Non renseigné'}</div></div>
          </div>
        </div>
        <div class="section">
          <div class="section-title">Coordonnées</div>
          <div class="grid">
            <div><div class="field-label">Email</div><div class="field-value ${!member.email ? 'empty' : ''}">${member.email || 'Non renseigné'}</div></div>
            <div><div class="field-label">Téléphone</div><div class="field-value ${!member.phone ? 'empty' : ''}">${member.phone || 'Non renseigné'}</div></div>
            <div><div class="field-label">Tél. mobile</div><div class="field-value ${!member.phone_mobile ? 'empty' : ''}">${member.phone_mobile || 'Non renseigné'}</div></div>
            <div><div class="field-label">Tél. domicile</div><div class="field-value ${!member.phone_home ? 'empty' : ''}">${member.phone_home || 'Non renseigné'}</div></div>
            <div><div class="field-label">Adresse</div><div class="field-value ${!member.address ? 'empty' : ''}">${member.address || 'Non renseignée'}${member.address_number ? ' ' + member.address_number : ''}</div></div>
            <div><div class="field-label">Code postal</div><div class="field-value ${!member.postal_code ? 'empty' : ''}">${member.postal_code || '—'}</div></div>
            <div><div class="field-label">Localité</div><div class="field-value ${!member.city ? 'empty' : ''}">${member.city || '—'}</div></div>
          </div>
        </div>
        <div class="section">
          <div class="section-title">Contact d'urgence</div>
          <div class="grid">
            <div><div class="field-label">Nom du contact</div><div class="field-value ${!member.emergency_contact ? 'empty' : ''}">${member.emergency_contact || 'Non renseigné'}</div></div>
            <div><div class="field-label">Contact d'urgence</div><div class="field-value ${!member.emergency_contact ? 'empty' : ''}">${member.emergency_contact || 'Non renseigné'}</div></div>
          </div>
        </div>
        <div class="footer">
          <span>Street Sport Luxembourg</span>
          <span>Imprimé le ${new Date().toLocaleString('fr-FR')}</span>
        </div>
      </body></html>
    `
    const win = window.open('', '_blank')
    win.document.write(printContent)
    win.document.close()
    win.focus()
    setTimeout(() => { win.print(); win.close() }, 300)
  }

  // ─── Stats ────────────────────────────────────────────────────────────────
  const fetchMemberStats = async (member) => {
    setStatsLoading(true); setStatsData(null)
    try {
      const { data, error } = await supabase
        .from('presences')
        .select(`id, timestamp, mode, sessions (id, date, course_types (id, name, day_of_week))`)
        .eq('participant_id', member.id).order('timestamp', { ascending: true })
      if (error) throw error
      const presences = data || []
      const totalSessions = presences.length
      const sportMap = {}
      presences.forEach((p) => {
        const s = p.sessions?.course_types?.name || 'Non défini'
        sportMap[s] = (sportMap[s] || 0) + 1
      })
      const sportStats = Object.entries(sportMap)
        .map(([sport, count]) => ({ sport, count, percent: totalSessions > 0 ? Math.round((count / totalSessions) * 100) : 0 }))
        .sort((a, b) => b.count - a.count)
      const dayMap = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }
      presences.forEach((p) => {
        const d = p.sessions?.date || p.timestamp
        if (d) dayMap[new Date(d).getDay()]++
      })
      const dayStats = Object.entries(dayMap)
        .map(([day, count]) => ({ day: DAYS_FR[parseInt(day)], count, percent: totalSessions > 0 ? Math.round((count / totalSessions) * 100) : 0 }))
        .filter(d => d.count > 0).sort((a, b) => b.count - a.count)
      setStatsData({
        totalSessions, sportStats, dayStats,
        firstAttendance: presences.length > 0 ? new Date(presences[0].timestamp).toLocaleDateString('fr-FR') : null,
        lastAttendance: presences.length > 0 ? new Date(presences[presences.length - 1].timestamp).toLocaleDateString('fr-FR') : null
      })
    } catch (error) {
      console.error('Erreur stats:', error)
      setStatsData({ error: true })
    } finally {
      setStatsLoading(false)
    }
  }

  const openStatsModal = (member) => {
    setStatsMember(member)
    setShowStatsModal(true)
    fetchMemberStats(member)
  }

  // ─── Édition membre ───────────────────────────────────────────────────────
  const openEditModal = (member) => {
    setEditMember({ ...member })
    setEditSuccess(false)
    setShowEditModal(true)
  }

  const handleEditChange = (field, value) => {
    setEditMember(prev => ({ ...prev, [field]: value }))
  }

  const saveEditMember = async () => {
    setEditSaving(true)
    setEditSuccess(false)
    try {
      const EDITABLE_FIELDS = [
        'first_name', 'last_name', 'birth_date', 'sex', 'nationality', 'marital_status',
        'email', 'phone', 'phone_mobile', 'phone_home',
        'address', 'address_number', 'postal_code', 'city', 'emergency_contact'
      ]
      const payload = {}
      EDITABLE_FIELDS.forEach(f => { payload[f] = editMember[f] || null })

      const { error } = await supabase
        .from('participants')
        .update(payload)
        .eq('id', editMember.id)
      if (error) throw error

      // Mettre à jour la liste locale sans rechargement
      setMembers(prev => prev.map(m => m.id === editMember.id ? { ...m, ...payload } : m))
      setEditSuccess(true)
      setTimeout(() => setShowEditModal(false), 1200)
    } catch (err) {
      console.error('Erreur sauvegarde:', err)
      alert('Erreur lors de la sauvegarde : ' + err.message)
    } finally {
      setEditSaving(false)
    }
  }

  // ─── Fusion doublons ──────────────────────────────────────────────────────
  const detectDuplicates = (list) => {
    const source = list || members
    const groups = {}
    source.forEach((m) => {
      const key = `${(m.last_name || '').toLowerCase().trim()}|${(m.first_name || '').toLowerCase().trim()}|${m.birth_date || ''}`
      if (!groups[key]) groups[key] = []
      groups[key].push(m)
    })
    return Object.values(groups).filter((g) => g.length > 1)
  }

  const openMergeModal = () => {
    setDuplicateGroups(detectDuplicates())
    setMergeResult(null)
    setShowMergeModal(true)
  }

  const mergeTwoMembers = async (keepId, deleteId) => {
    setMerging(true)
    setMergeResult(null)
    try {
      const keep     = members.find((m) => m.id === keepId)
      const toDelete = members.find((m) => m.id === deleteId)

      const MERGE_FIELDS = [
        'first_name', 'last_name', 'birth_date', 'sex', 'nationality', 'marital_status',
        'email', 'phone', 'phone_mobile', 'phone_home',
        'address', 'address_number', 'postal_code', 'city',
        'emergency_contact', 'token'
      ]
      const merged = {}
      MERGE_FIELDS.forEach((f) => {
        merged[f] = keep[f] || toDelete[f] || null
      })

      const { error: updateErr } = await supabase
        .from('participants').update(merged).eq('id', keepId)
      if (updateErr) throw updateErr

      // ✅ CORRECTION : fonction SQL qui supprime d'abord les présences en double
      // (même session sur les deux profils) puis transfère le reste.
      const { error: presErr } = await supabase
        .rpc('merge_participant_presences', { keep_id: keepId, delete_id: deleteId })
      if (presErr) throw presErr

      const { error: delErr } = await supabase
        .from('participants').delete().eq('id', deleteId)
      if (delErr) throw delErr

      const { data: freshData } = await supabase
        .from('participants').select('*').order('created_at', { ascending: false })
      const freshList = freshData || []
      setMembers(freshList)
      setDuplicateGroups(detectDuplicates(freshList))
      setMergeResult({ success: true, name: `${keep.first_name} ${keep.last_name}` })
    } catch (err) {
      console.error('Erreur fusion:', err)
      setMergeResult({ success: false, error: err.message })
    } finally {
      setMerging(false)
    }
  }

  // ─── Export CSV ───────────────────────────────────────────────────────────
  const exportMembersCSV = () => {
    const csvContent = [
      ['Prénom', 'Nom', 'Date de naissance', 'Pays', 'Date inscription', 'Profil complet'],
      ...filteredMembers.map(m => [
        m.first_name, m.last_name, m.birth_date || '',
        m.nationality || '',
        new Date(m.created_at).toLocaleDateString('fr-FR'),
        isProfileComplete(m) ? 'Oui' : 'Non'
      ])
    ].map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `membres_street_sport_${new Date().toLocaleDateString('fr-FR')}.csv`)
    document.body.appendChild(link); link.click(); document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // ─── QR Code présence ─────────────────────────────────────────────────────
  const downloadQRCode = (member) => { setSelectedMember(member); setShowQRModal(true) }

  const handlePrintQRCode = () => {
    const printWindow = window.open('', '_blank')
    printWindow.document.write(`<!DOCTYPE html><html><head><title>QR Code - ${selectedMember.first_name} ${selectedMember.last_name}</title><style>body{font-family:Arial,sans-serif;text-align:center;padding:20px;}@media print{body{margin:0;padding:10px;}}</style></head><body><h2>Street Sport Luxembourg</h2><h3>${selectedMember.first_name} ${selectedMember.last_name}</h3><p>QR Code de présence</p><svg width="300" height="300" viewBox="0 0 300 300">${document.getElementById('qr-code-svg').innerHTML}</svg><p><small>Présentez ce QR code à chaque session pour marquer votre présence</small></p></body></html>`)
    printWindow.document.close(); printWindow.print()
  }

  const downloadQRCodeAsPNG = () => {
    const svg = document.getElementById('qr-code-svg')
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas'); const ctx = canvas.getContext('2d'); const img = new Image()
    img.onload = () => {
      canvas.width = img.width; canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      const dl = document.createElement('a')
      dl.download = `qrcode_${selectedMember.first_name}_${selectedMember.last_name}.png`
      dl.href = canvas.toDataURL('image/png'); dl.click()
    }
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  // ─── QR Code lien de profil ───────────────────────────────────────────────
  const openProfileQRModal = (member) => { setProfileQRMember(member); setShowProfileQRModal(true) }

  const generateProfileLink = (member) => `${window.location.origin}/complete-profile?token=${member.token}`

  const copyProfileLink = async (member) => {
    try { await navigator.clipboard.writeText(generateProfileLink(member)); alert('Lien copié dans le presse-papier !') }
    catch (err) { prompt('Copiez ce lien:', generateProfileLink(member)) }
  }

  const sendProfileLinkByEmail = (member) => {
    const link = generateProfileLink(member)
    window.location.href = `mailto:${member.email || ''}?subject=${encodeURIComponent('Complétez votre profil Street Sport Luxembourg')}&body=${encodeURIComponent(`Bonjour ${member.first_name},\n\nMerci de compléter votre profil en cliquant sur le lien ci-dessous :\n\n${link}\n\nCordialement,\nL'équipe Street Sport Luxembourg`)}`
  }

  const handlePrintProfileQRCode = () => {
    const link = generateProfileLink(profileQRMember)
    const svgEl = document.getElementById('profile-qr-code-svg')
    if (!svgEl) return
    const printWindow = window.open('', '_blank')
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Lien profil - ${profileQRMember.first_name} ${profileQRMember.last_name}</title><style>*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;text-align:center;padding:32px;color:#212529;}.logo{font-size:2rem;margin-bottom:6px;}h2{color:#4f46e5;font-size:18px;margin-bottom:4px;}h3{font-size:22px;margin-bottom:2px;color:#111;}.subtitle{color:#6b7280;font-size:13px;margin-bottom:24px;}.qr-wrapper{display:inline-block;padding:16px;border:3px solid #4f46e5;border-radius:16px;background:#fff;margin-bottom:24px;}.link-box{background:#f0f4ff;border:1px solid #c7d2fe;border-radius:8px;padding:10px 16px;font-size:11px;color:#4f46e5;word-break:break-all;margin:0 auto 20px;max-width:420px;}.instructions{background:#fefce8;border:1px solid #fde68a;border-radius:8px;padding:10px 16px;font-size:12px;color:#92400e;max-width:420px;margin:0 auto 24px;text-align:left;}.footer{font-size:11px;color:#9ca3af;margin-top:20px;}@media print{body{padding:16px;}}</style></head><body><div class="logo">🥊</div><h2>Street Sport Luxembourg</h2><h3>${profileQRMember.first_name} ${profileQRMember.last_name}</h3><p class="subtitle">Complétion de profil</p><div class="qr-wrapper"><svg width="240" height="240" viewBox="0 0 240 240">${svgEl.innerHTML}</svg></div><div class="instructions"><strong>📱 Comment compléter votre profil :</strong><br/>1. Scannez ce QR code avec votre téléphone<br/>2. Remplissez le formulaire qui s'affiche<br/>3. Validez vos informations</div><div class="link-box">🔗 Lien direct : ${link}</div><div class="footer">Généré le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</div></body></html>`)
    printWindow.document.close(); printWindow.focus()
    setTimeout(() => { printWindow.print(); printWindow.close() }, 300)
  }

  const downloadProfileQRCodeAsPNG = () => {
    const svg = document.getElementById('profile-qr-code-svg'); if (!svg) return
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas'); const ctx = canvas.getContext('2d'); const img = new Image()
    img.onload = () => {
      canvas.width = img.width; canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      const dl = document.createElement('a')
      dl.download = `profil_qrcode_${profileQRMember.first_name}_${profileQRMember.last_name}.png`
      dl.href = canvas.toDataURL('image/png'); dl.click()
    }
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  // ─── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <ProtectedRoute><Layout user={{}}>
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Chargement...</span></div>
        <p className="mt-3">Chargement des membres...</p>
      </Container>
    </Layout></ProtectedRoute>
  )

  // ─── Composant champ de la fiche ──────────────────────────────────────────
  const Field = ({ label, value }) => (
    <div className="mb-3">
      <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: '0.92rem', color: value ? '#111' : '#d1d5db', fontStyle: value ? 'normal' : 'italic', borderBottom: '1px solid #f3f4f6', paddingBottom: '4px' }}>
        {value || 'Non renseigné'}
      </div>
    </div>
  )

  // ─── Composant cellule de comparaison ────────────────────────────────────
  const CellValue = ({ value, other, differ, bg }) => {
    const willReceive = !value && !!other
    const isConflict  = differ && !!value && !!other && value !== other

    return (
      <td style={{
        padding: '7px 14px', verticalAlign: 'middle',
        borderBottom: '1px solid #f3f4f6', borderLeft: '1px solid #f3f4f6',
        background: willReceive ? '#eff6ff' : isConflict ? '#fffbeb' : bg,
        borderLeftWidth: willReceive || isConflict ? '3px' : '1px',
        borderLeftColor: willReceive ? '#93c5fd' : isConflict ? '#fbbf24' : '#f3f4f6',
        borderLeftStyle: 'solid'
      }}>
        <span style={{ color: value ? '#111' : '#d1d5db', fontStyle: value ? 'normal' : 'italic', fontSize: '0.82rem' }}>
          {value || '—'}
        </span>
        {willReceive && (
          <span style={{ marginLeft: 6, fontSize: '0.65rem', color: '#1d4ed8', background: '#dbeafe', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>
            ← sera complété
          </span>
        )}
        {isConflict && (
          <span style={{ marginLeft: 6, fontSize: '0.65rem', color: '#92400e', background: '#fef3c7', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>
            ⚠ valeurs différentes
          </span>
        )}
      </td>
    )
  }

  // ─── Nombre de doublons pour le badge du bouton ───────────────────────────
  const duplicateCount = detectDuplicates().length

  // ─── Mineurs (< 18 ans) dont l'inscription n'est pas terminée ─────────────
  const minorsIncompleteCount = members.filter(isMinorIncomplete).length

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <ProtectedRoute>
      <Layout user={{}}>
        <Container fluid="lg">

          {/* ── En-tête ──────────────────────────────────────────────────── */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>Gestion des Membres</h2>
              <p className="text-muted mb-0">Liste de tous les participants inscrits</p>
            </div>
            <div className="d-flex gap-2 flex-wrap justify-content-end">
              <Button
                variant="warning"
                onClick={openMergeModal}
                className="position-relative"
              >
                🔀 Fusionner les doublons
                {duplicateCount > 0 && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: '0.65rem' }}
                  >
                    {duplicateCount}
                  </span>
                )}
              </Button>
              <Button variant="success" onClick={exportMembersCSV}>📊 Exporter CSV</Button>
            </div>
          </div>

          {/* ── Alerte doublons ───────────────────────────────────────────── */}
          {duplicateCount > 0 && (
            <Alert variant="warning" className="d-flex align-items-center gap-2 mb-4 py-2">
              <span style={{ fontSize: '1.1rem' }}>⚠️</span>
              <div className="flex-grow-1">
                <strong>{duplicateCount} groupe(s) de doublons</strong> détecté(s) dans la liste.
                {' '}<span className="text-muted small">Même nom, prénom et date de naissance.</span>
              </div>
              <Button variant="warning" size="sm" onClick={openMergeModal}>Fusionner</Button>
            </Alert>
          )}

          {/* ── Filtres ───────────────────────────────────────────────────── */}
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <Row className="g-3 align-items-center">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>🔍</InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Rechercher par nom, prénom ou pays..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>✕</Button>
                    )}
                  </InputGroup>
                </Col>
                <Col md={6} className="d-flex flex-wrap gap-2 justify-content-md-end align-items-center">
                  <Badge bg="primary" className="fs-6">{filteredMembers.length} membre(s)</Badge>
                  <Badge bg="success" className="fs-6">{filteredMembers.filter(isProfileComplete).length} profil(s) complet(s)</Badge>
                  <OverlayTrigger placement="top" overlay={<Tooltip>Moins de 18 ans, profil incomplet. Cliquer pour {minorsOnly ? 'afficher tous les membres' : 'afficher uniquement ces membres'}.</Tooltip>}>
                    <Badge
                      bg={minorsOnly ? 'dark' : 'danger'}
                      className="fs-6"
                      role="button"
                      onClick={() => setMinorsOnly(prev => !prev)}
                      style={{ cursor: 'pointer', outline: minorsOnly ? '2px solid #dc3545' : 'none' }}
                    >
                      🧒 {minorsIncompleteCount} mineur(s) à compléter{minorsOnly ? ' ✕' : ''}
                    </Badge>
                  </OverlayTrigger>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* ── Tableau ───────────────────────────────────────────────────── */}
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">👥 Liste des Membres</h4>
            </Card.Header>
            <Card.Body className="p-0">
              {filteredMembers.length > 0 ? (
                <Table responsive hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Nom</th><th>Prénom</th><th>Date de naissance</th><th>Pays</th>
                      <th>Date d'inscription</th><th className="text-center">Statut</th>
                      <th className="text-center">QR Code</th><th className="text-center">Participation</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((member) => {
                      const profileComplete = isProfileComplete(member)
                      const { percent } = getProfileCompletionFields(member)
                      const isDuplicate = duplicateGroups.some(g => g.some(m => m.id === member.id))
                      return (
                        <tr
                          key={member.id}
                          style={isDuplicate ? { background: '#fffbeb', borderLeft: '3px solid #f59e0b' } : undefined}
                        >
                          <td className="fw-bold">
                            {member.last_name}
                            {isDuplicate && (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Doublon détecté — cliquez sur "Fusionner"</Tooltip>}>
                                <span className="ms-1" style={{ fontSize: '0.7rem', color: '#f59e0b', cursor: 'help' }}>⚠️</span>
                              </OverlayTrigger>
                            )}
                          </td>
                          <td className="fw-medium">{member.first_name}</td>
                          <td>
                            {member.birth_date
                              ? new Date(member.birth_date).toLocaleDateString('fr-FR')
                              : <span className="text-muted fst-italic">Non renseignée</span>}
                          </td>
                          <td>{member.nationality || <span className="text-muted fst-italic">Non renseigné</span>}</td>
                          <td>
                            {new Date(member.created_at).toLocaleDateString('fr-FR')}<br />
                            <small className="text-muted">{new Date(member.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
                          </td>

                          {/* ── Statut ── */}
                          <td className="text-center">
                            {profileComplete ? (
                              <OverlayTrigger placement="top" overlay={<Tooltip>Cliquer pour voir la fiche complète</Tooltip>}>
                                <Badge
                                  bg="success"
                                  onClick={() => openProfileModal(member)}
                                  style={{ cursor: 'pointer', fontSize: '0.78rem', padding: '6px 10px', transition: 'opacity 0.2s' }}
                                  onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
                                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                                >
                                  ✓ Complet 👁
                                </Badge>
                              </OverlayTrigger>
                            ) : (
                              <div>
                                <Badge bg="warning" text="dark" style={{ fontSize: '0.75rem', marginBottom: '3px', display: 'block' }}>⚠ Incomplet</Badge>
                                <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>{percent}% renseigné</div>
                              </div>
                            )}
                          </td>

                          {/* ── QR présence ── */}
                          <td className="text-center">
                            <Button variant="outline-success" size="sm" onClick={() => downloadQRCode(member)} title="Voir le QR Code de présence">
                              📷 QR
                            </Button>
                          </td>

                          {/* ── Stats ── */}
                          <td className="text-center">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir la participation par sport et par jour</Tooltip>}>
                              <Button
                                size="sm"
                                onClick={() => openStatsModal(member)}
                                style={{ borderColor: '#6f42c1', color: '#6f42c1', fontWeight: '600', background: 'transparent', border: '1px solid #6f42c1', borderRadius: '4px' }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#6f42c1'; e.currentTarget.style.color = '#fff' }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6f42c1' }}
                              >
                                📈 Stats
                              </Button>
                            </OverlayTrigger>
                          </td>

                          {/* ── Actions ── */}
                          <td className="text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Modifier le profil</Tooltip>}>
                                <Button variant="outline-primary" size="sm" onClick={() => openEditModal(member)}>✏️</Button>
                              </OverlayTrigger>

                              <OverlayTrigger placement="top" overlay={<Tooltip>QR Code du lien de complétion de profil</Tooltip>}>
                                <Button variant={profileComplete ? 'outline-secondary' : 'outline-warning'} size="sm" onClick={() => openProfileQRModal(member)}>🔗 QR</Button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Copier le lien de complétion</Tooltip>}>
                                <Button variant="outline-secondary" size="sm" onClick={() => copyProfileLink(member)}>📋</Button>
                              </OverlayTrigger>
                              {member.email && (
                                <OverlayTrigger placement="top" overlay={<Tooltip>Envoyer par email</Tooltip>}>
                                  <Button variant="outline-info" size="sm" onClick={() => sendProfileLinkByEmail(member)}>✉️</Button>
                                </OverlayTrigger>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-5">
                  <div style={{ fontSize: '4rem' }}>👥</div>
                  <p className="mt-3 text-muted">{(searchTerm || minorsOnly) ? 'Aucun membre trouvé' : 'Aucun membre inscrit'}</p>
                  {(searchTerm || minorsOnly) && <Button variant="outline-primary" onClick={() => { setSearchTerm(''); setMinorsOnly(false) }}>Réinitialiser la recherche</Button>}
                </div>
              )}
            </Card.Body>
          </Card>


          {/* ════════════════════════════════════════════════════════════════
              Modal — Édition du profil membre
              ════════════════════════════════════════════════════════════════ */}
          <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered scrollable>
            <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #1d4ed8, #4f46e5)', color: '#fff' }}>
              <div>
                <Modal.Title className="fw-bold fs-5">
                  ✏️ Modifier le profil — {editMember?.first_name} {editMember?.last_name}
                </Modal.Title>
                <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 2 }}>
                  Modifiez les informations puis cliquez sur Enregistrer
                </div>
              </div>
            </Modal.Header>

            <Modal.Body className="p-0">
              {editMember && (
                <>
                  {/* Barre de complétion en temps réel */}
                  {(() => {
                    const { filled, total, percent } = getProfileCompletionFields(editMember)
                    return (
                      <div className="px-4 py-3 bg-light border-bottom">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="text-muted fw-semibold">Complétion du profil</small>
                          <small className="fw-bold" style={{ color: percent > 70 ? '#16a34a' : percent > 40 ? '#f59e0b' : '#ef4444' }}>
                            {filled}/{total} champs — {percent}%
                          </small>
                        </div>
                        <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                          <div
                            className="progress-bar"
                            style={{
                              width: `${percent}%`,
                              background: percent > 70 ? '#16a34a' : percent > 40 ? '#f59e0b' : '#ef4444',
                              transition: 'width 0.3s ease'
                            }}
                          />
                        </div>
                      </div>
                    )
                  })()}

                  {/* Succès */}
                  {editSuccess && (
                    <Alert variant="success" className="mx-4 mt-3 mb-0 py-2">
                      ✅ <strong>Profil enregistré avec succès !</strong>
                    </Alert>
                  )}

                  {/* ── Identité ── */}
                  <div className="px-4 py-3 border-bottom">
                    <h6 className="fw-bold mb-3" style={{ color: '#1d4ed8' }}>👤 Informations personnelles</h6>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Nom *</Form.Label>
                        <Form.Control
                          value={editMember.last_name || ''}
                          onChange={e => handleEditChange('last_name', e.target.value)}
                          placeholder="Nom de famille"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Prénom *</Form.Label>
                        <Form.Control
                          value={editMember.first_name || ''}
                          onChange={e => handleEditChange('first_name', e.target.value)}
                          placeholder="Prénom"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Date de naissance *</Form.Label>
                        <Form.Control
                          type="date"
                          value={editMember.birth_date || ''}
                          onChange={e => handleEditChange('birth_date', e.target.value)}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Sexe</Form.Label>
                        <Form.Select
                          value={editMember.sex || ''}
                          onChange={e => handleEditChange('sex', e.target.value)}
                        >
                          <option value="">— Non renseigné —</option>
                          <option value="Homme">Homme</option>
                          <option value="Femme">Femme</option>
                          <option value="Autre">Autre</option>
                        </Form.Select>
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Nationalité</Form.Label>
                        <Form.Control
                          value={editMember.nationality || ''}
                          onChange={e => handleEditChange('nationality', e.target.value)}
                          placeholder="Ex: Luxembourgeois"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>État civil</Form.Label>
                        <Form.Select
                          value={editMember.marital_status || ''}
                          onChange={e => handleEditChange('marital_status', e.target.value)}
                        >
                          <option value="">— Non renseigné —</option>
                          <option value="Célibataire">Célibataire</option>
                          <option value="Marié(e)">Marié(e)</option>
                          <option value="Divorcé(e)">Divorcé(e)</option>
                          <option value="Veuf/Veuve">Veuf/Veuve</option>
                          <option value="Pacsé(e)">Pacsé(e)</option>
                        </Form.Select>
                      </Col>
                    </Row>
                  </div>

                  {/* ── Coordonnées ── */}
                  <div className="px-4 py-3 border-bottom">
                    <h6 className="fw-bold mb-3 text-primary">📍 Coordonnées</h6>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={editMember.email || ''}
                          onChange={e => handleEditChange('email', e.target.value)}
                          placeholder="adresse@email.com"
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Téléphone</Form.Label>
                        <Form.Control
                          value={editMember.phone || ''}
                          onChange={e => handleEditChange('phone', e.target.value)}
                          placeholder="+352 ..."
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Tél. mobile</Form.Label>
                        <Form.Control
                          value={editMember.phone_mobile || ''}
                          onChange={e => handleEditChange('phone_mobile', e.target.value)}
                          placeholder="+352 ..."
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Tél. domicile</Form.Label>
                        <Form.Control
                          value={editMember.phone_home || ''}
                          onChange={e => handleEditChange('phone_home', e.target.value)}
                          placeholder="+352 ..."
                        />
                      </Col>
                      <Col md={8}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Adresse</Form.Label>
                        <Form.Control
                          value={editMember.address || ''}
                          onChange={e => handleEditChange('address', e.target.value)}
                          placeholder="Rue, avenue..."
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>N° adresse</Form.Label>
                        <Form.Control
                          value={editMember.address_number || ''}
                          onChange={e => handleEditChange('address_number', e.target.value)}
                          placeholder="42"
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Code postal</Form.Label>
                        <Form.Control
                          value={editMember.postal_code || ''}
                          onChange={e => handleEditChange('postal_code', e.target.value)}
                          placeholder="1234"
                        />
                      </Col>
                      <Col md={8}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>Localité</Form.Label>
                        <Form.Control
                          value={editMember.city || ''}
                          onChange={e => handleEditChange('city', e.target.value)}
                          placeholder="Luxembourg, Esch-sur-Alzette..."
                        />
                      </Col>
                    </Row>
                  </div>

                  {/* ── Contact d'urgence ── */}
                  <div className="px-4 py-3">
                    <h6 className="fw-bold mb-3 text-danger">🆘 Contact d'urgence</h6>
                    <Row className="g-3">
                      <Col md={12}>
                        <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>
                          Nom + téléphone du contact
                        </Form.Label>
                        <Form.Control
                          value={editMember.emergency_contact || ''}
                          onChange={e => handleEditChange('emergency_contact', e.target.value)}
                          placeholder="Ex: Marie Dupont — +352 661 000 000"
                        />
                      </Col>
                    </Row>
                  </div>
                </>
              )}
            </Modal.Body>

            <Modal.Footer className="d-flex justify-content-between align-items-center">
              <small className="text-muted fst-italic">* Champs requis pour un profil complet</small>
              <div className="d-flex gap-2">
                <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={editSaving}>
                  Annuler
                </Button>
                <Button variant="primary" onClick={saveEditMember} disabled={editSaving || editSuccess}>
                  {editSaving
                    ? <><Spinner size="sm" animation="border" className="me-1" />Enregistrement...</>
                    : editSuccess
                      ? '✅ Enregistré !'
                      : '💾 Enregistrer'
                  }
                </Button>
              </div>
            </Modal.Footer>
          </Modal>


          {/* ════════════════════════════════════════════════════════════════
              Modal — Fusion des doublons
              ════════════════════════════════════════════════════════════════ */}
          <Modal show={showMergeModal} onHide={() => setShowMergeModal(false)} size="xl" centered scrollable>
            <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #ca8a04, #ea580c)', color: '#fff' }}>
              <div>
                <Modal.Title className="fw-bold fs-5">🔀 Fusion des doublons</Modal.Title>
                <div style={{ fontSize: '0.78rem', opacity: 0.9, marginTop: 2 }}>
                  Détection basée sur nom + prénom + date de naissance
                </div>
              </div>
            </Modal.Header>

            <Modal.Body>
              {mergeResult && (
                <Alert
                  variant={mergeResult.success ? 'success' : 'danger'}
                  className="mb-3"
                  dismissible
                  onClose={() => setMergeResult(null)}
                >
                  {mergeResult.success
                    ? <>✅ <strong>Fusion réussie</strong> pour {mergeResult.name}. Les présences ont été transférées et le doublon supprimé.</>
                    : <>❌ <strong>Erreur lors de la fusion :</strong> {mergeResult.error}</>
                  }
                </Alert>
              )}

              {duplicateGroups.length === 0 ? (
                <div className="text-center py-5">
                  <div style={{ fontSize: '3.5rem' }}>✅</div>
                  <p className="mt-3 fw-bold text-success fs-5">Aucun doublon détecté !</p>
                  <p className="text-muted small">Tous les membres ont une combinaison unique de nom, prénom et date de naissance.</p>
                </div>
              ) : (
                <>
                  <Alert variant="warning" className="py-2 mb-4">
                    <small>
                      ⚠️ <strong>{duplicateGroups.length} groupe(s) de doublons</strong> détecté(s).
                      Pour chaque groupe, choisissez le profil à <strong>conserver</strong>.
                      Les données manquantes et toutes les présences de l'autre profil seront fusionnées, puis le doublon sera supprimé définitivement.
                    </small>
                  </Alert>

                  {duplicateGroups.map((group, gi) => {
                    const m0 = group[0]
                    const m1 = group[1]
                    const p0 = getProfileCompletionFields(m0)
                    const p1 = getProfileCompletionFields(m1)

                    const SECTIONS = [
                      {
                        title: '👤 Identité',
                        rows: [
                          { label: 'Nom',               k0: m0.last_name,          k1: m1.last_name },
                          { label: 'Prénom',            k0: m0.first_name,         k1: m1.first_name },
                          { label: 'Date naissance',    k0: m0.birth_date ? new Date(m0.birth_date).toLocaleDateString('fr-FR') : null, k1: m1.birth_date ? new Date(m1.birth_date).toLocaleDateString('fr-FR') : null },
                          { label: 'Sexe',              k0: m0.sex,                k1: m1.sex },
                          { label: 'Nationalité',       k0: m0.nationality,        k1: m1.nationality },
                          { label: 'État civil',        k0: m0.marital_status,     k1: m1.marital_status },
                        ]
                      },
                      {
                        title: '📍 Coordonnées',
                        rows: [
                          { label: 'Email',             k0: m0.email,              k1: m1.email },
                          { label: 'Téléphone',         k0: m0.phone,              k1: m1.phone },
                          { label: 'Tél. mobile',       k0: m0.phone_mobile,       k1: m1.phone_mobile },
                          { label: 'Tél. domicile',     k0: m0.phone_home,         k1: m1.phone_home },
                          { label: 'Adresse',           k0: m0.address,            k1: m1.address },
                          { label: 'N° adresse',        k0: m0.address_number,     k1: m1.address_number },
                          { label: 'Code postal',       k0: m0.postal_code,        k1: m1.postal_code },
                          { label: 'Localité',          k0: m0.city,               k1: m1.city },
                        ]
                      },
                      {
                        title: '🆘 Contact d\'urgence',
                        rows: [
                          { label: 'Contact urgence',   k0: m0.emergency_contact,  k1: m1.emergency_contact },
                        ]
                      },
                      {
                        title: '📋 Inscription',
                        rows: [
                          { label: 'Date inscription',  k0: new Date(m0.created_at).toLocaleDateString('fr-FR') + ' ' + new Date(m0.created_at).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}), k1: new Date(m1.created_at).toLocaleDateString('fr-FR') + ' ' + new Date(m1.created_at).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}) },
                          { label: 'Token QR',          k0: m0.token ? m0.token.slice(0,16)+'…' : null, k1: m1.token ? m1.token.slice(0,16)+'…' : null },
                        ]
                      },
                    ]

                    return (
                      <Card key={gi} className="mb-4 border-0 shadow-sm" style={{ overflow: 'hidden' }}>
                        <Card.Header style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '12px 16px' }}>
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                            <div className="flex-grow-1">
                              <span className="fw-bold">Doublon #{gi + 1} —</span>{' '}
                              <span className="fw-semibold">{m0.first_name} {m0.last_name}</span>
                              {m0.birth_date && (
                                <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                  Né·e le {new Date(m0.birth_date).toLocaleDateString('fr-FR')}
                                </Badge>
                              )}
                              <Badge bg="danger" className="ms-2" style={{ fontSize: '0.7rem' }}>{group.length} entrées</Badge>
                            </div>
                          </div>
                        </Card.Header>

                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                            <thead>
                              <tr>
                                <th style={{ width: '18%', padding: '10px 14px', background: '#f8f9fa', borderBottom: '2px solid #e9ecef', color: '#6b7280', fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Champ
                                </th>
                                {[m0, m1].map((m, ci) => {
                                  const pct = ci === 0 ? p0.percent : p1.percent
                                  const other_pct = ci === 0 ? p1.percent : p0.percent
                                  const isBest = pct >= other_pct
                                  return (
                                    <th
                                      key={m.id}
                                      style={{
                                        width: '41%',
                                        padding: '10px 14px',
                                        background: isBest ? '#f0fdf4' : '#f8f9fa',
                                        borderBottom: `2px solid ${isBest ? '#86efac' : '#e9ecef'}`,
                                        borderLeft: '1px solid #e9ecef'
                                      }}
                                    >
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className="fw-bold" style={{ color: '#111', fontSize: '0.85rem' }}>
                                          Profil {ci + 1}
                                          {isBest && (
                                            <span style={{ marginLeft: 6, background: '#16a34a', color: '#fff', fontSize: '0.6rem', fontWeight: 700, padding: '1px 7px', borderRadius: '20px', verticalAlign: 'middle' }}>
                                              ★ Le plus complet
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 3 }}>
                                        Inscrit le {new Date(m.created_at).toLocaleDateString('fr-FR')} à {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                      <div style={{ marginTop: 6 }}>
                                        <div className="d-flex justify-content-between" style={{ fontSize: '0.67rem', color: '#9ca3af', marginBottom: 3 }}>
                                          <span>{ci === 0 ? p0.filled : p1.filled}/{ci === 0 ? p0.total : p1.total} champs</span>
                                          <span className="fw-bold">{pct}%</span>
                                        </div>
                                        <div style={{ height: 5, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                          <div style={{ height: '100%', width: `${pct}%`, background: pct > 70 ? '#16a34a' : pct > 40 ? '#f59e0b' : '#ef4444', borderRadius: 3 }} />
                                        </div>
                                      </div>
                                    </th>
                                  )
                                })}
                              </tr>
                            </thead>

                            <tbody>
                              {SECTIONS.map((section) => (
                                <React.Fragment key={section.title}>
                                  <tr>
                                    <td
                                      colSpan={3}
                                      style={{ padding: '8px 14px', background: '#f1f5f9', fontWeight: 700, fontSize: '0.72rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}
                                    >
                                      {section.title}
                                    </td>
                                  </tr>
                                  {section.rows.map((row, ri) => {
                                    const v0 = row.k0
                                    const v1 = row.k1
                                    const differ = v0 !== v1 && (v0 || v1)
                                    const rowBg = ri % 2 === 0 ? '#fff' : '#fafafa'
                                    return (
                                      <tr key={row.label} style={{ background: rowBg }}>
                                        <td style={{ padding: '7px 14px', color: '#6b7280', fontWeight: 600, fontSize: '0.75rem', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' }}>
                                          {row.label}
                                        </td>
                                        <CellValue value={v0} other={v1} differ={differ} bg={rowBg} />
                                        <CellValue value={v1} other={v0} differ={differ} bg={rowBg} />
                                      </tr>
                                    )
                                  })}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div style={{ padding: '14px 16px', background: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
                          <div
                            className="mb-3 p-2 rounded"
                            style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', fontSize: '0.75rem', color: '#4f46e5' }}
                          >
                            💡 <strong>Choisissez le profil à conserver.</strong> Les champs vides seront complétés avec les données de l'autre.
                            Toutes les présences sont transférées. Le doublon est supprimé définitivement.
                          </div>
                          <Row className="g-2">
                            {[m0, m1].map((member, ci) => {
                              const otherMember = ci === 0 ? m1 : m0
                              const pct = ci === 0 ? p0.percent : p1.percent
                              const isBest = pct >= (ci === 0 ? p1.percent : p0.percent)
                              return (
                                <Col key={member.id} md={6}>
                                  <Button
                                    variant={isBest ? 'success' : 'outline-secondary'}
                                    className="w-100"
                                    disabled={merging}
                                    onClick={() => {
                                      if (window.confirm(
                                        `Conserver le Profil ${ci + 1} (inscrit le ${new Date(member.created_at).toLocaleDateString('fr-FR')}) ?\n\n` +
                                        `L'autre profil sera supprimé après transfert de toutes ses présences.\n` +
                                        `Les champs manquants seront complétés avec les données de l'autre profil.`
                                      )) {
                                        mergeTwoMembers(member.id, otherMember.id)
                                      }
                                    }}
                                  >
                                    {merging
                                      ? <><Spinner size="sm" animation="border" className="me-1" />Fusion en cours...</>
                                      : <>✓ Conserver le Profil {ci + 1}{isBest ? ' ★' : ''}</>
                                    }
                                  </Button>
                                </Col>
                              )
                            })}
                          </Row>
                        </div>
                      </Card>
                    )
                  })}
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowMergeModal(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal>


          {/* ════════════════════════════════════════════════════════════════
              Modal — Fiche membre complète
              ════════════════════════════════════════════════════════════════ */}
          <Modal show={showProfileModal} onHide={() => setShowProfileModal(false)} size="lg" centered>
            <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #15803d, #16a34a)', color: '#fff' }}>
              <div>
                <Modal.Title className="fw-bold fs-5">
                  👤 Fiche membre — {profileMember?.first_name} {profileMember?.last_name}
                </Modal.Title>
                <div style={{ fontSize: '0.78rem', opacity: 0.85, marginTop: 2 }}>
                  Membre depuis le {profileMember && new Date(profileMember.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {profileMember?.nationality && ` • ${profileMember.nationality}`}
                </div>
              </div>
            </Modal.Header>

            <Modal.Body className="p-0">
              {profileMember && (() => {
                const age = profileMember.birth_date
                  ? Math.floor((new Date() - new Date(profileMember.birth_date)) / (365.25 * 24 * 3600 * 1000))
                  : null
                const { filled, total, percent } = getProfileCompletionFields(profileMember)
                return (
                  <>
                    <div className="px-4 py-3 bg-light border-bottom d-flex align-items-center gap-3 flex-wrap">
                      <Badge bg="success" style={{ fontSize: '0.8rem', padding: '5px 12px' }}>✓ Profil complet</Badge>
                      <div className="flex-grow-1" style={{ minWidth: '120px' }}>
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.72rem', color: '#6b7280' }}>
                          <span>{filled} / {total} champs renseignés</span>
                          <span>{percent}%</span>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div className="progress-bar bg-success" style={{ width: `${percent}%` }} />
                        </div>
                      </div>
                      {age !== null && (
                        <Badge bg={age < 18 ? 'warning' : 'info'} text={age < 18 ? 'dark' : undefined} style={{ fontSize: '0.78rem' }}>
                          {age < 18 ? '🧒 Mineur' : '🧑 Majeur'} ({age} ans)
                        </Badge>
                      )}
                    </div>

                    <div className="px-4 py-3 border-bottom">
                      <h6 className="fw-bold mb-3" style={{ color: '#15803d' }}>👤 Informations personnelles</h6>
                      <Row>
                        <Col md={6}><Field label="Nom" value={profileMember.last_name} /></Col>
                        <Col md={6}><Field label="Prénom" value={profileMember.first_name} /></Col>
                        <Col md={6}><Field label="Date de naissance" value={profileMember.birth_date ? new Date(profileMember.birth_date).toLocaleDateString('fr-FR') : null} /></Col>
                        <Col md={6}><Field label="Sexe" value={profileMember.sex} /></Col>
                        <Col md={6}><Field label="Nationalité" value={profileMember.nationality} /></Col>
                        <Col md={6}><Field label="État civil" value={profileMember.marital_status} /></Col>
                      </Row>
                    </div>

                    <div className="px-4 py-3 border-bottom">
                      <h6 className="fw-bold mb-3 text-primary">📍 Coordonnées</h6>
                      <Row>
                        <Col md={6}><Field label="Email" value={profileMember.email} /></Col>
                        <Col md={6}><Field label="Téléphone" value={profileMember.phone} /></Col>
                        <Col md={6}><Field label="Tél. mobile" value={profileMember.phone_mobile} /></Col>
                        <Col md={6}><Field label="Tél. domicile" value={profileMember.phone_home} /></Col>
                        <Col md={6}><Field label="Adresse" value={profileMember.address ? `${profileMember.address}${profileMember.address_number ? ' ' + profileMember.address_number : ''}` : null} /></Col>
                        <Col md={6}><Field label="Code postal" value={profileMember.postal_code} /></Col>
                        <Col md={6}><Field label="Localité" value={profileMember.city} /></Col>
                      </Row>
                    </div>

                    <div className="px-4 py-3">
                      <h6 className="fw-bold mb-3 text-danger">🆘 Contact d'urgence</h6>
                      <Row>
                        <Col md={6}><Field label="Contact d'urgence" value={profileMember.emergency_contact} /></Col>
                      </Row>
                    </div>
                  </>
                )
              })()}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="primary" onClick={() => { setShowProfileModal(false); openEditModal(profileMember) }}>✏️ Modifier</Button>
              <Button variant="success" onClick={() => printMemberProfile(profileMember)}>🖨️ Imprimer la fiche</Button>
              <Button variant="secondary" onClick={() => setShowProfileModal(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal>


          {/* ── Modal QR Code présence ───────────────────────────────────────── */}
          <Modal show={showQRModal} onHide={() => setShowQRModal(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>🎫 QR Code - {selectedMember?.first_name} {selectedMember?.last_name}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
              {selectedMember && (
                <>
                  <div className="mb-4">
                    <h4 className="mb-1">{selectedMember.first_name} {selectedMember.last_name}</h4>
                    <p className="text-muted mb-0">
                      {selectedMember.nationality && `${selectedMember.nationality} • `}
                      Membre depuis le {new Date(selectedMember.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="mb-4 d-flex justify-content-center">
                    <QRCodeSVG
                      id="qr-code-svg"
                      value={selectedMember.token}
                      size={256}
                      level="H"
                      includeMargin
                      style={{ border: '10px solid white', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                    />
                  </div>
                  <Alert variant="info" className="mb-4">
                    <small><strong>📱 Instructions :</strong> Présentez ce QR code à chaque session pour marquer votre présence automatiquement.</small>
                  </Alert>
                  <div className="d-grid gap-2 d-md-flex justify-content-center mb-4">
                    <Button variant="primary" onClick={handlePrintQRCode} size="lg">🖨️ Imprimer</Button>
                    <Button variant="success" onClick={downloadQRCodeAsPNG} size="lg">📥 Télécharger PNG</Button>
                  </div>
                  <div className="p-3 bg-light rounded text-start">
                    <Row>
                      <Col md={6}>
                        <small className="text-muted d-block mb-2">
                          <strong>Token unique :</strong><br />
                          <code className="text-break">{selectedMember.token}</code>
                        </small>
                      </Col>
                      <Col md={6}>
                        <small className="text-muted d-block mb-2">
                          <strong>Profil :</strong><br />
                          {isProfileComplete(selectedMember) ? (
                            <Badge
                              bg="success"
                              style={{ cursor: 'pointer' }}
                              onClick={() => { setShowQRModal(false); openProfileModal(selectedMember) }}
                            >
                              ✓ Complet — Voir la fiche 👁
                            </Badge>
                          ) : (
                            <>
                              <Badge bg="warning" text="dark" className="me-2">⚠ Incomplet</Badge>
                              <Button variant="link" size="sm" className="p-0" onClick={() => copyProfileLink(selectedMember)}>Copier le lien</Button>
                            </>
                          )}
                        </small>
                      </Col>
                    </Row>
                  </div>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowQRModal(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal>


          {/* ── Modal QR Code lien de profil ────────────────────────────────── */}
          <Modal show={showProfileQRModal} onHide={() => setShowProfileQRModal(false)} size="md" centered>
            <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: '#fff' }}>
              <Modal.Title className="fs-6 fw-bold">
                🔗 QR Code — Lien de profil
                <div style={{ fontSize: '0.78rem', opacity: 0.85, fontWeight: 400, marginTop: 2 }}>
                  {profileQRMember?.first_name} {profileQRMember?.last_name}
                </div>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center py-4">
              {profileQRMember && (
                <>
                  {isProfileComplete(profileQRMember)
                    ? <Alert variant="success" className="py-2 mb-3"><small>✅ Le profil de ce membre est déjà <strong>complet</strong>.</small></Alert>
                    : <Alert variant="warning" className="py-2 mb-3"><small>⚠️ Ce membre doit encore <strong>compléter son profil</strong>. Partagez ce QR code.</small></Alert>
                  }
                  <div
                    className="d-inline-block mb-3"
                    style={{ padding: '16px', border: '3px solid #4f46e5', borderRadius: '16px', background: '#fff', boxShadow: '0 4px 20px rgba(79,70,229,0.12)' }}
                  >
                    <QRCodeSVG
                      id="profile-qr-code-svg"
                      value={generateProfileLink(profileQRMember)}
                      size={220}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                  <div
                    className="mb-3 p-2 rounded"
                    style={{ background: '#f0f4ff', border: '1px solid #c7d2fe', fontSize: '0.72rem', color: '#4f46e5', wordBreak: 'break-all' }}
                  >
                    {generateProfileLink(profileQRMember)}
                  </div>
                  <div
                    className="text-start p-3 mb-4 rounded"
                    style={{ background: '#fefce8', border: '1px solid #fde68a', fontSize: '0.8rem', color: '#92400e' }}
                  >
                    <strong>📱 Instructions :</strong>
                    <ol className="mb-0 mt-1 ps-3">
                      <li>Scannez ce QR code avec votre téléphone</li>
                      <li>Remplissez le formulaire qui s'affiche</li>
                      <li>Validez vos informations personnelles</li>
                    </ol>
                  </div>
                  <div className="d-flex gap-2 justify-content-center flex-wrap">
                    <Button variant="primary" onClick={handlePrintProfileQRCode}>🖨️ Imprimer</Button>
                    <Button variant="success" onClick={downloadProfileQRCodeAsPNG}>📥 PNG</Button>
                    <Button variant="outline-primary" onClick={() => copyProfileLink(profileQRMember)}>📋 Copier le lien</Button>
                    {profileQRMember.email && (
                      <Button variant="outline-info" onClick={() => sendProfileLinkByEmail(profileQRMember)}>✉️ Email</Button>
                    )}
                  </div>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowProfileQRModal(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal>


          {/* ── Modal Statistiques ───────────────────────────────────────────── */}
          <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)} size="lg" centered>
            <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #6f42c1, #4f46e5)', color: '#fff' }}>
              <Modal.Title>📈 Statistiques — {statsMember?.first_name} {statsMember?.last_name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {statsLoading && (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Chargement des statistiques...</p>
                </div>
              )}
              {!statsLoading && statsData?.error && (
                <Alert variant="danger">Impossible de charger les statistiques.</Alert>
              )}
              {!statsLoading && statsData && !statsData.error && (
                <>
                  <Row className="mb-4 g-3">
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 rounded" style={{ background: '#f0f4ff', border: '1px solid #c7d2fe' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#4f46e5' }}>{statsData.totalSessions}</div>
                        <small className="text-muted fw-semibold">Sessions totales</small>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#16a34a' }}>{statsData.sportStats.length}</div>
                        <small className="text-muted fw-semibold">Sports pratiqués</small>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 rounded" style={{ background: '#fefce8', border: '1px solid #fde68a' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#ca8a04' }}>{statsData.firstAttendance || '—'}</div>
                        <small className="text-muted fw-semibold">1ère présence</small>
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-center p-3 rounded" style={{ background: '#fdf2f8', border: '1px solid #f9a8d4' }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#db2777' }}>{statsData.lastAttendance || '—'}</div>
                        <small className="text-muted fw-semibold">Dernière présence</small>
                      </div>
                    </Col>
                  </Row>

                  {statsData.totalSessions === 0 ? (
                    <Alert variant="info">Ce membre n'a encore aucune présence enregistrée.</Alert>
                  ) : (
                    <>
                      <Card className="mb-4 border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom fw-bold">🏅 Taux de participation par sport</Card.Header>
                        <Card.Body>
                          {statsData.sportStats.map((s, i) => (
                            <div key={s.sport} className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold">{s.sport}</span>
                                <span className="text-muted small">{s.count} session{s.count > 1 ? 's' : ''} — <strong>{s.percent}%</strong></span>
                              </div>
                              <ProgressBar now={s.percent} style={{ height: '12px', borderRadius: '6px', backgroundColor: '#e9ecef' }}>
                                <div style={{ width: `${s.percent}%`, background: getSportColor(i), borderRadius: '6px', transition: 'width 0.6s ease' }} />
                              </ProgressBar>
                            </div>
                          ))}
                        </Card.Body>
                      </Card>

                      <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom fw-bold">📅 Jours de participation habituels</Card.Header>
                        <Card.Body>
                          <Row className="g-2">
                            {DAYS_FR.map((dayName) => {
                              const found = statsData.dayStats.find(d => d.day === dayName)
                              const isActive = !!found
                              return (
                                <Col key={dayName} xs={6} sm={4} md={3}>
                                  <div
                                    className="text-center p-2 rounded"
                                    style={{
                                      background: isActive ? '#4f46e5' : '#f8f9fa',
                                      color: isActive ? '#fff' : '#adb5bd',
                                      border: `2px solid ${isActive ? '#4f46e5' : '#e9ecef'}`
                                    }}
                                  >
                                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{isActive ? found.count : 0}</div>
                                    <small style={{ fontWeight: 600 }}>{dayName}</small>
                                    {isActive && <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{found.percent}%</div>}
                                  </div>
                                </Col>
                              )
                            })}
                          </Row>
                        </Card.Body>
                      </Card>
                    </>
                  )}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowStatsModal(false)}>Fermer</Button>
            </Modal.Footer>
          </Modal>

        </Container>
      </Layout>
    </ProtectedRoute>
  )
}

export default Members